import React, { useState, useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api, { getUserFacingErrorMessage } from '../utils/api';
import { generateCodeVerifierAndChallenge, buildAuthUrl } from '../utils/oidc';
import { getOAuthRedirectUri } from '../utils/oauthRedirectOrigin';
import { Button, Card, Input, Stack, Text } from '../ui';
import PasswordRequirements from './PasswordRequirements';
import { passwordMeetsPolicy, passwordPolicyError } from '../utils/passwordPolicy';

/**
 * Nominee invite acceptance screen.
 *
 * This page is reached via a magic link sent to the nominee:
 *   /nominee-invite?token=...
 *
 * The token is a random identifier recorded in Aurora's nominee_invites table
 * and is used by the backend to look up the invited email, owner, and
 * relationship. We never trust any email passed from the browser – the token
 * is the only identifier we send here.
 */
export default function NomineeInvite() {
  const location = useLocation();
  const navigate = useNavigate();
  const token = useMemo(() => {
    try {
      const params = new URLSearchParams(location.search || '');
      return params.get('token') || '';
    } catch {
      return '';
    }
  }, [location.search]);

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [marketing, setMarketing] = useState(false);
  const [agreeTos, setAgreeTos] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showTos, setShowTos] = useState(false);
  const [inviteInfo, setInviteInfo] = useState({
    loading: true,
    valid: true,
    hasAccount: false,
    emailMasked: '',
    relationship: '',
    ownerDisplayName: '',
  });

  const validate = () => {
    if (!token) return 'This invite link is missing its token. Please check the link in your email.';
    if (!inviteInfo.valid) return 'This invite is no longer valid. Please ask the person who invited you to send a new invite.';
    if (!name.trim()) return 'Full name is required';
    if (!password) return 'Password is required';
    if (!passwordMeetsPolicy(password)) return passwordPolicyError(password);
    if (password !== confirmPassword) return 'Passwords do not match';
    if (!agreeTos) return 'You must agree to the Terms of Service';
    if (!agreePrivacy) return 'You must confirm you have read the Privacy Policy';
    return '';
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    const v = validate();
    if (v) { setError(v); return; }

    try {
      setSubmitting(true);
      // Use the invite token issued when the owner created the nominee.
      // The backend uses this to look up the email, owner_sub and relationship
      // in Aurora and will also mark the invite as accepted if successful.
      const res = await api.post('/auth/signup-cognito/nominee-invite', {
        token,
        name: name.trim(),
        password,
        marketingConsent: marketing,
        tosVersion: '2025-09-01',
        privacyVersion: '2025-09-01',
      });

      if (res.data && res.data.success) {
        try {
          sessionStorage.setItem(
            'signup_login_notice_v1',
            'Your nominee account was created. Sign in below with the email and password you just chose.',
          );
        } catch {}
        setSuccess('Account created — redirecting to sign in…');
        setTimeout(() => navigate('/login'), 1500);
      } else {
        setError('We could not complete your registration. Please try again.');
      }
    } catch (err) {
      // Surface a concise, user-friendly error from the backend when available
      // so that common Cognito failures (duplicate account, weak password,
      // expired invite, etc.) are clearly explained, while still falling back
      // to a generic message if we cannot classify the issue.
      const apiReason = err?.response?.data?.reason;
      console.error('Nominee invite signup error:', err?.response?.data || err?.message || apiReason || 'Unknown');
      setError(getUserFacingErrorMessage(err, {
        fallback: 'We could not complete your registration. Your invite may have expired or already been used.',
        includeSupportForUnknown: false,
      }));
    } finally {
      setSubmitting(false);
    }
  };

  const inviteInvalid = !token || !inviteInfo.valid;

  useEffect(() => {
    // Look up the invite to determine whether it is still valid and whether
    // the invited email already has an account in Cognito. This lets us steer
    // existing users towards login rather than showing a duplicate signup.
    if (!token) {
      setInviteInfo((prev) => ({ ...prev, loading: false, valid: false }));
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setInviteInfo((prev) => ({ ...prev, loading: true }));
        const res = await api.get('/auth/nominee-invite', { params: { token } });
        if (cancelled) return;
        setInviteInfo({
          loading: false,
          valid: !!res.data?.valid,
          hasAccount: !!res.data?.hasAccount,
          emailMasked: res.data?.emailMasked || '',
          relationship: res.data?.relationship || '',
          ownerDisplayName: res.data?.ownerDisplayName || '',
        });
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load invite info:', err?.response?.data || err?.message);
        const status = err?.response?.status;
        // Treat 4xx errors as invalid/expired/used invites.
        if (status && status >= 400 && status < 500) {
          setInviteInfo({
            loading: false,
            valid: false,
            hasAccount: false,
            emailMasked: '',
            relationship: '',
            ownerDisplayName: '',
          });
        } else {
          setInviteInfo((prev) => ({ ...prev, loading: false }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--mist-100)' }}>
      <Card style={{ padding: 32, minWidth: 340, width: 420, maxWidth: '92vw' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
          <img src="/logo.svg" alt="ProVault" style={{ height: 28 }} />
        </div>
        <Text as="h1" size="xl" weight={800} style={{ margin: '0 0 10px' }}>
          Accept your nominee invite
        </Text>
        <Text size="sm" muted style={{ marginBottom: 16 }}>
          {inviteInfo.ownerDisplayName ? (
            <>
              <strong>{inviteInfo.ownerDisplayName}</strong>
              {inviteInfo.hasAccount
                ? ' has invited you to ProVault as a nominee. Log in to your existing account to accept.'
                : ' has invited you to ProVault as a nominee. Create your account to securely view what they share with you.'}
            </>
          ) : (
            inviteInfo.hasAccount
              ? 'Log in to your existing ProVault account to accept this nominee invite.'
              : 'Create your ProVault nominee account to securely view information that has been shared with you.'
          )}
        </Text>

        {inviteInvalid && (
          <div role="alert" className="pv-error" style={{ fontWeight: 600, marginBottom: 16 }}>
            This invite link is missing its token, incomplete, or no longer valid. Please open the link directly from your email, or contact the person who invited you to ProVault.
          </div>
        )}

        {!inviteInvalid && !inviteInfo.loading && !inviteInfo.hasAccount && (
          <form onSubmit={onSubmit} noValidate aria-describedby={error ? 'invite-form-error' : undefined}>
            <Stack gap={12}>
              <Input
                id="inv-name"
                type="text"
                label="Full Name"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Jane Doe"
                required
              />

              <Input
                id="inv-password"
                type="password"
                label="Password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Create a strong password"
                minLength={12}
                aria-describedby="inv-password-requirements"
                required
              />
              <PasswordRequirements password={password} id="inv-password-requirements" />

              <Input
                id="inv-confirm"
                type="password"
                label="Confirm Password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Re-enter password"
                minLength={12}
                required
              />

              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 4 }}>
                <input
                  id="inv-marketing"
                  type="checkbox"
                  checked={marketing}
                  onChange={e => setMarketing(e.target.checked)}
                />
                <label htmlFor="inv-marketing" style={{ fontSize: 14 }}>
                  Send me product updates and tips (optional)
                </label>
              </div>

              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 4 }}>
                <input
                  id="inv-tos"
                  type="checkbox"
                  checked={agreeTos}
                  onChange={e => setAgreeTos(e.target.checked)}
                  aria-describedby="inv-tos-help"
                  required
                />
                <label htmlFor="inv-tos" style={{ fontSize: 14 }}>
                  I agree to the{' '}
                  <button
                    type="button"
                    onClick={() => setShowTos(true)}
                    style={{ background: 'none', border: 'none', padding: 0, color: 'var(--sage-600)', textDecoration: 'underline', cursor: 'pointer' }}
                  >
                    Terms of Service
                  </button>
                </label>
              </div>
              <div id="inv-tos-help" style={{ fontSize: 12, color: 'var(--dusk-500)' }}>
                Review the summary below.{' '}
                <a
                  href="https://provault.io/terms"
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: 'var(--sage-600)' }}
                >
                  View full Terms
                </a>.
              </div>

              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 4 }}>
                <input
                  id="inv-privacy"
                  type="checkbox"
                  checked={agreePrivacy}
                  onChange={e => setAgreePrivacy(e.target.checked)}
                  required
                />
                <label htmlFor="inv-privacy" style={{ fontSize: 14 }}>
                  I&apos;ve read the{' '}
                  <a
                    href="https://provault.io/privacy"
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: 'var(--sage-600)', textDecoration: 'underline' }}
                  >
                    Privacy Policy
                  </a>
                </label>
              </div>

              {error && (
                <div id="invite-form-error" role="alert" className="pv-error" style={{ fontWeight: 600, marginTop: 4 }}>
                  {error}
                </div>
              )}
              {success && (
                <div role="status" style={{ color: '#2e7d32', fontWeight: 500, marginTop: 4 }}>
                  {success}
                </div>
              )}

              <Button type="submit" disabled={submitting} style={{ marginTop: 8, width: '100%' }}>
                {submitting ? 'Creating...' : 'Create account'}
              </Button>
            </Stack>
          </form>
        )}

        {inviteInfo.loading && !inviteInvalid && (
          <div style={{ textAlign: 'center', fontSize: 14, marginTop: 12 }}>
            Checking your invite…
          </div>
        )}

        {!inviteInvalid && inviteInfo.hasAccount && !inviteInfo.loading && (
          <div style={{ marginTop: 12 }}>
            {error && (
              <div id="invite-form-error" role="alert" className="pv-error" style={{ fontWeight: 600, marginBottom: 8 }}>
                {error}
              </div>
            )}
            <div style={{ fontSize: 14, color: 'var(--dusk-700)', marginBottom: 10 }}>
              This invite was sent to <strong>{inviteInfo.emailMasked || 'your email address'}</strong>. You already have a ProVault
              account for this email. Log in to link this invite to your existing account.
            </div>
            <Button
              type="button"
              onClick={async () => {
                try {
                  setError('');
                  const { verifier, challenge } = await generateCodeVerifierAndChallenge();
                  sessionStorage.setItem('pkce_verifier', verifier);
                  const domain = (typeof window !== 'undefined' && window.__COGNITO_DOMAIN__) || process.env.REACT_APP_COGNITO_DOMAIN;
                  const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
                  const redirectUri = getOAuthRedirectUri();
                  const scope = 'openid email profile';
                  // Preserve the invite token through the OAuth redirect so the
                  // callback can attach this invite to the authenticated user.
                  try {
                    sessionStorage.setItem('pending_nominee_invite_token', token);
                  } catch {}
                  const url = buildAuthUrl({
                    domain,
                    clientId,
                    redirectUri,
                    scope,
                    codeChallenge: challenge,
                  });
                  window.location.assign(url);
                } catch (e) {
                  console.error('Failed to redirect to Cognito login for nominee invite:', e);
                  setError('We could not start the login flow. Please try again.');
                }
              }}
              style={{ marginTop: 8, width: '100%' }}
            >
              Continue to login
            </Button>
          </div>
        )}

        {showTos && (
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="inv-tos-title"
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.35)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000,
            }}
          >
            <div
              style={{
                background: '#fff',
                borderRadius: 12,
                width: 560,
                maxWidth: '90vw',
                maxHeight: '80vh',
                boxShadow: 'var(--shadow-soft)',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--mist-200)' }}>
                <h2 id="inv-tos-title" style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
                  ProVault – Terms of Service
                </h2>
                <div style={{ fontSize: 12, color: 'var(--dusk-500)' }}>Effective date: 1st September 2025</div>
              </div>
              <div style={{ padding: 16, overflow: 'auto' }}>
                <p>Welcome to ProVault. By creating an account, you agree to the following terms:</p>
                <h3>Who we are</h3>
                <p>ProVault lets you securely store and manage financial accounts, insurance, investments and other important information.</p>
                <h3>Using ProVault</h3>
                <ul>
                  <li>You must be at least 16 years old.</li>
                  <li>You are responsible for keeping your login details safe. Do not share your account.</li>
                  <li>Do not use ProVault for illegal purposes.</li>
                </ul>
                <h3>Your Data</h3>
                <p>
                  You own your data. We process and store it so that the service can work. We apply strict security measures to protect your
                  information. Please review our Privacy Policy to understand how we handle your personal data.
                </p>
                <h3>Subscriptions &amp; Payment</h3>
                <p>
                  ProVault may be free during early access. Paid plans (if introduced later) will be clearly communicated with pricing and
                  renewal terms.
                </p>
                <h3>Availability &amp; Changes</h3>
                <p>
                  We aim for high availability but cannot guarantee uninterrupted service. We may change, suspend or stop features as we
                  improve the service. We will notify you in advance of major changes.
                </p>
                <h3>Ending Your Account</h3>
                <p>
                  You can delete your account at any time. We may suspend or close accounts that breach these terms or are inactive for long
                  periods.
                </p>
                <h3>Limitation of Liability</h3>
                <p>
                  ProVault is provided &quot;as is&quot; without guarantees. We are not liable for indirect or incidental damages. Our total
                  liability will not exceed the amount you&apos;ve paid to us in the last 12 months (if any).
                </p>
                <h3>Changes to These Terms</h3>
                <p>
                  We may update these Terms from time to time. We will notify you of material changes by email or within the app. Continuing
                  to use ProVault after changes means you accept the new Terms.
                </p>
                <h3>Contact Us</h3>
                <p>If you have questions, contact us at: support@provault.io</p>
                <p>
                  <a
                    href="https://provault.io/terms"
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: 'var(--sage-700)' }}
                  >
                    View full Terms
                  </a>
                </p>
              </div>
              <div
                style={{
                  padding: 12,
                  borderTop: '1px solid var(--mist-200)',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 8,
                }}
              >
                <Button type="button" variant="secondary" onClick={() => setShowTos(false)}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}


